import type { RouteRecordRaw } from '@fusapp-work/shell'
import { useAuthFlowState } from '../composables/useAuthFlowState'
import { registrationFlowSnapshot } from '../composables/useRegistrationFlow'

/**
 * FW-126 kabul kriteri 1 — dort kare, dort ayri rota: email -> password ->
 * pick -> done. Her adim kendi karesinin verisine (girilen e-posta, login-by-
 * email'in dondurdugu kiraci listesi, secilen kiraci) ihtiyac duyar; dogrudan
 * URL'e gidilirse (ya da sayfa yenilenirse — bkz. useAuthFlowState.ts) bu
 * veri yoktur. `beforeEnter` guard'lari eksik onkosulda kullaniciyi baslangica
 * (email) geri yollar; boylece bir sonraki adim asla yarim veriyle mount
 * olmaz.
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'email',
    component: () => import('../views/EmailView.vue'),
  },
  {
    path: '/password',
    name: 'password',
    component: () => import('../views/PasswordView.vue'),
    beforeEnter: () => {
      const { state } = useAuthFlowState()
      if (state.email === '') return { name: 'email' }
      return true
    },
  },
  {
    path: '/pick',
    name: 'pick',
    component: () => import('../views/PickView.vue'),
    beforeEnter: () => {
      const { state } = useAuthFlowState()
      if (state.exchangeToken === null || state.tenants.length === 0) return { name: 'email' }
      return true
    },
  },
  {
    path: '/done',
    name: 'done',
    component: () => import('../views/DoneView.vue'),
    beforeEnter: () => {
      const { state } = useAuthFlowState()
      if (state.selectedTenantId === null) return { name: 'email' }
      return true
    },
  },
  /**
   * FW-175 (FW-25 dilim 3) — davet kareleri 14/15/16. Tenant SPA rotasinda
   * tasinir (bkz. useInvitationPeek.ts basligi — davet token'i tek basina
   * hangi kiraciya ait oldugunu SOYLEMEZ, backend'in `[Route("api/v{version}/
   * {tenantId}/registration")]` rotasi tenantId'yi ZORUNLU ister). Bu karar
   * "main"a bayraklandi (FW-175 raporu); onayli tasarimin adres cubugu metni
   * ("id.fusapp.com/invite/{token}") sadelestirme sayildi — diger onayli
   * ekranlar da ayni sekilde ("id.fusapp.com/register") tenant'i URL'de
   * GOSTERMEZ ama backend rotasi ister.
   *
   * 16-davet-mevcut icin AYRI bir rota YOK: bu kare PIKSEL PIKSEL 02-giris-
   * parola ile ayni (bkz. PasswordView.vue basligi) — InviteView'in "Evet"
   * dali dogrudan standart `/` (email) -> `/password` akisina yonlendirir,
   * baglam useAuthFlowState.setInvitationContext ile tasinir.
   */
  {
    path: '/invite/:tenantId/:token',
    name: 'invite',
    component: () => import('../views/InviteView.vue'),
  },
  {
    path: '/invite/:tenantId/:token/register',
    name: 'invite-register',
    component: () => import('../views/InviteRegisterView.vue'),
    beforeEnter: (to) => {
      const { state } = useAuthFlowState()
      // Davet baglami (InviteView'in basarili peek()'i) yoksa — dogrudan
      // derin-baglanti, sayfa yenileme (bkz. useAuthFlowState.ts basligi
      // "sayfa yenilenirse bu durum kaybolur") ya da gecersiz token — once
      // 14'e geri gonderilir, orada token yeniden dogrulanir.
      if (state.invitationToken === null) {
        return { name: 'invite', params: { tenantId: to.params.tenantId, token: to.params.token } }
      }
      return true
    },
  },
  /**
   * FW-129 — 11/12/13-kayit-* (tenant'siz genel kayit, AuthTrialPrompt.vue'nun
   * "Ücretsiz deneyin" karti buraya baglanir). Giris akisinin dort AYRI
   * rotasiyla (`/`, `/password`, `/pick`, `/done`) AYNI desen: her adim
   * kendi karesinin verisine ihtiyac duyar; dogrudan URL'e gidilirse (ya da
   * sayfa yenilenirse) bu veri yoktur. Guard'lar `registrationFlowSnapshot()`
   * (SAF okuma — bkz. o fonksiyonun basligi, `useRegistrationFlow()`nun
   * KENDISI burada GUVENLI DEGILDIR) ile eksik onkosulda kullaniciyi bir
   * onceki adima geri yollar.
   */
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/RegisterIdentityView.vue'),
  },
  {
    path: '/register/otp',
    name: 'register-otp',
    component: () => import('../views/RegisterOtpView.vue'),
    beforeEnter: () => {
      if (registrationFlowSnapshot().registrationToken === null) return { name: 'register' }
      return true
    },
  },
  {
    path: '/register/password',
    name: 'register-password',
    component: () => import('../views/RegisterPasswordView.vue'),
    beforeEnter: () => {
      const snapshot = registrationFlowSnapshot()
      if (snapshot.registrationToken === null) return { name: 'register' }
      if (!snapshot.otpVerified) return { name: 'register-otp' }
      return true
    },
  },
]
